import dagre from 'dagre';
import React from 'react';
import {
  ConnectionLineType,
  Edge as FlowEdge,
  MarkerType,
  Node as FlowNode,
  Position,
} from 'react-flow-renderer';

// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ClusterNode} from './ClusterNode';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {DefaultNode} from './DefaultNode';
import {artifactMembershipNodeInputType} from './InfoDrawerArtifacts';
import {runNodeInputType} from './InfoDrawerRuns';
import {Artifact, Dag, Direction, Run} from './utils';

export const NODEWIDTH = 200;
export const NODEHEIGHT = 50;
export const CLUSTERHEIGHT = 300;

export enum nodeType {
  RUN = 'Run',
  ARTIFACT = 'Artifact',
}

export enum clusterNodeType {
  RUN,
  ARTIFACT,
}

export const extractArtifactMembershipNodeInput = (
  artifact: Artifact
): artifactMembershipNodeInputType => {
  const [entityName, projectName, artifactType, artifactWithCommitHash] =
    artifact.artifactNodeID.split('/');
  const [artifactName, artifactCommitHash] = artifactWithCommitHash.split(':');
  return {
    artifactID: artifact.id,
    artifactVersionIndex: artifact.versionIndex,
    artifactType,
    artifactCommitHash,
  };
};

export const extractRunPath = (runNodeID: string): string => {
  const [entity, project, runID] = runNodeID.split('/');
  return ['', entity, project, 'runs', runID, 'overview'].join('/');
};

export const extractRunNodeInputType = (run: Run): runNodeInputType => {
  const [entityName, projectName, runName] = run.runNodeID.split('/');
  const jobType = run.jobType != null ? run.jobType : '';
  const runDisplayName = run.displayName != null ? run.displayName : '';
  return {
    entityName,
    projectName,
    runName,
    jobType,
    runDisplayName,
  };
};

// We use dagre as the layouting engine.
// https://reactflow.dev/docs/examples/layout/dagre/
export const getLayoutedElements = (
  nodes: FlowNode[],
  clusterNodes: FlowNode[],
  edges: FlowEdge[],
  direction = 'LR',
  artifactID: string,
  expandedArtifact: string,
  expandedRun: string
): {
  nodes: FlowNode[];
  edges: FlowEdge[];
  selectedNode?: FlowNode;
  expandedNode?: FlowNode;
} => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({rankdir: direction});

  nodes.forEach(node => {
    dagreGraph.setNode(node.id, {width: NODEWIDTH, height: NODEHEIGHT});
  });

  clusterNodes.forEach(node => {
    dagreGraph.setNode(node.id, {
      width: NODEWIDTH,
      height: CLUSTERHEIGHT,
    });
  });

  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  let selectedNode;
  let expandedNode;
  nodes.forEach(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = Position.Left;
    node.sourcePosition = Position.Right;

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - NODEWIDTH / 2,
      y: nodeWithPosition.y - NODEHEIGHT / 2,
    };

    if (node.id === artifactID) {
      selectedNode = node;
    }
    if (node.id === expandedArtifact) {
      expandedNode = node;
    } else if (node.id === expandedRun) {
      expandedNode = node;
    }

    return node;
  });

  clusterNodes.forEach(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = Position.Left;
    node.sourcePosition = Position.Right;
    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - NODEWIDTH / 2,
      y: nodeWithPosition.y - CLUSTERHEIGHT / 2,
    };

    if (node.id === artifactID) {
      selectedNode = node;
    }

    return node;
  });
  nodes = nodes.concat(clusterNodes);

  return {nodes, edges, selectedNode, expandedNode};
};

export function dagToFlowEls(
  dag: Dag,
  artifactID: string,
  expandedArtifact: string,
  expandedRun: string,
  updateDag: (dag: Dag, expandedArtifact: string, expandedRun: string) => void,
  setIsDrawerOpen: (val: boolean) => void,
  setDrawerType: (val: nodeType) => void,
  setArtifactMembershipNodeInput: (
    val: artifactMembershipNodeInputType
  ) => void,
  setRunNodeInput: (val: runNodeInputType) => void,
  filterGeneratedArtifacts: boolean,
  directLineage: boolean,
  clusterSize?: number
): any {
  const {artifacts, runs, edges, clusters} = dag;

  const nodes: FlowNode[] = [];
  const clusterNodes: FlowNode[] = [];
  const flowEdges: FlowEdge[] = [];
  const position = {x: 0, y: 0};
  const edgeType = ConnectionLineType.SmoothStep;

  Object.entries(clusters).forEach(([cid, cluster], _) => {
    if (cluster != null) {
      if (
        (cluster.nodeType === nodeType.ARTIFACT &&
          cluster.artifactNodes.length > 0) ||
        (cluster.nodeType === nodeType.RUN && cluster.runNodes.length > 0)
      ) {
        // hide cluster if all nodes have been removed
        clusterNodes.push({
          id: cid,
          data: {
            label: (
              <ClusterNode
                cluster={cluster}
                updateDag={updateDag}
                setIsDrawerOpen={setIsDrawerOpen}
                setDrawerType={setDrawerType}
                setArtifactMembershipNodeInput={setArtifactMembershipNodeInput}
                setRunNodeInput={setRunNodeInput}
                key={cid}
                filterGeneratedArtifacts={filterGeneratedArtifacts}
                clusterSize={clusterSize}
                directLineage={directLineage}
              />
            ),
          },
          position,
          style: {
            width: NODEWIDTH + 'px',
            height: CLUSTERHEIGHT + 'px',
            padding: '0px',
            border: 'none',
            borderRadius: '8px',
            textAlign: 'left',
            boxShadow: 'none',
          },
        });
      }
    }
  });

  const nodeStyle = {
    width: NODEWIDTH + 'px',
    height: NODEHEIGHT + 'px',
    padding: '0px',
    border: 'none',
    borderRadius: '8px',
    boxShadow: 'none',
  };

  Object.entries(artifacts).forEach(([aid, artifact], _) => {
    const isSelected = aid === artifactID;
    const isExpandedNode = aid === expandedArtifact;
    if (artifact != null) {
      nodes.push({
        id: aid,
        data: {
          label: (
            <DefaultNode
              isSelected={isSelected}
              isExpandedNode={isExpandedNode}
              setIsDrawerOpen={setIsDrawerOpen}
              setDrawerType={setDrawerType}
              setArtifactMembershipNodeInput={setArtifactMembershipNodeInput}
              type={artifact.artifactTypeName}
              nodeName={
                artifact.artifactSequenceName + ':v' + artifact.versionIndex
              }
              artifact={artifact}
            />
          ),
        },
        position,
        style: nodeStyle,
      });
    }
  });

  Object.entries(runs).forEach(([rid, run]) => {
    const isExpandedNode = rid === expandedRun;
    if (run != null) {
      nodes.push({
        id: rid,
        data: {
          label: (
            <DefaultNode
              isSelected={false}
              isExpandedNode={isExpandedNode}
              setIsDrawerOpen={setIsDrawerOpen}
              setDrawerType={setDrawerType}
              setRunNodeInput={setRunNodeInput}
              type={run.jobType ?? ''}
              nodeName={run.displayName ?? run.runName}
              run={run}
            />
          ),
        },
        position,
        style: nodeStyle,
      });
    }
  });

  Object.entries(edges).forEach(([id, edge], _) => {
    if (edge != null) {
      let source = null;
      let target = null;
      source = edge.runNodeID !== '' ? edge.runNodeID : edge.clusterID;
      target =
        edge.artifactNodeID !== '' ? edge.artifactNodeID : edge.clusterID;
      if (edge.direction === Direction.AwayFromArtifact) {
        [source, target] = [target, source];
      }
      flowEdges.push({
        id,
        source,
        target,
        type: edgeType,
        markerEnd: {
          type: MarkerType.Arrow,
          width: 20,
          height: 30,
        },
      });
    }
  });

  return [nodes, clusterNodes, flowEdges];
}
